import { formattedCurrency, withStyles } from 'vopay';
import { FunctionComponent, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import LottieFile from '../../components/LottieFile';
import LottiePayment from '../../LottieFiles/Payments.json';
import ExpandCard from '../../components/ExpandCard';
import type { LottieRefCurrentProps } from 'lottie-react';
import styles from './InteractiveInfo.module.scss';
import { Title } from 'UI';
import { AccountLimits } from '@/hooks/useApi/useFetchAccountLimits';
import Skeleton from '../../widgets/Skeleton';

const cn = withStyles(styles);

type TotalPaymentsCardProps = {
  currency: string;
  accountLimits: AccountLimits | null;
  loading: boolean;
};

type FC = FunctionComponent<TotalPaymentsCardProps>;

const TotalPaymentsCard: FC = ({ currency, accountLimits, loading }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { onSubNavClick } = useNavMenu();
  const { permissions } = usePermissions();
  const paymentRef = useRef<LottieRefCurrentProps>(null);

  const handleLottieFileHover = (
    ref: RefObject<LottieRefCurrentProps | null>,
    isEnter: boolean,
    frameRange: [number, number] = [0, 45],
  ) => {
    if (!ref.current) return;

    ref.current.pause();
    const [start, end] = frameRange;

    if (isEnter) {
      ref.current.setDirection(1);
      ref.current.playSegments([start, end], true);
    } else {
      ref.current.setDirection(-1);
      ref.current.playSegments([end, start], true);
    }
  };

  const parseAmount = (value: string | undefined | null): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  return (
    <ExpandCard
      align="center"
      disabled={loading}
      buttonText={t('Widgets.InteractiveInfo.openSinglePayment')}
      showButton={permissions.CreatePayments}
      onMouseOver={() => handleLottieFileHover(paymentRef, true)}
      onMouseOut={() => handleLottieFileHover(paymentRef, false)}
      onNavigateClick={() => onSubNavClick('singlePayment', '/single-payment', t('payment'))}
      top={
        <div className={cn('totalBalanceTopContainer')}>
          <div className={cn('totalBalanceTop')}>
            <Title level="6" emphasized={false} className={cn('totalCardLabel')}>
              {t('Widgets.InteractiveInfo.totalMonthlyPayments')}
            </Title>

            {loading ? (
              <Title level="3" className={cn('totalCardAmount')}>
                <Skeleton />
              </Title>
            ) : accountLimits ? (
              <div className={cn('totalCardCurrency')}>
                <Title level="5" emphasized={false}>
                  {currency}
                </Title>

                <Title level="3" className={cn('totalCardAmount')}>
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits.Outbound?.Limits?.Monthly ?? 0) -
                        parseAmount(accountLimits.Outbound?.RemainingLimits?.Monthly ?? 0),
                    ),
                  )}
                </Title>
              </div>
            ) : null}
          </div>
          <LottieFile animationData={LottiePayment} lottieRef={paymentRef} />
        </div>
      }
      contents={
        <div className={cn('totalBalanceContents')}>
          {accountLimits ? (
            <>
              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.dailyPayments')}</div>
                <div>
                  {currency}{' '}
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits.Outbound?.Limits?.Daily ?? 0) -
                        parseAmount(accountLimits.Outbound?.RemainingLimits?.Daily ?? 0),
                    ),
                  )}
                </div>
              </div>

              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.weeklyPayments')}</div>
                <div>
                  {currency}{' '}
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits.Outbound?.Limits?.Weekly ?? 0) -
                        parseAmount(accountLimits.Outbound?.RemainingLimits?.Weekly ?? 0),
                    ),
                  )}
                </div>
              </div>

              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.monthlyPayments')}</div>
                <div>
                  {currency}{' '}
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits.Outbound?.Limits?.Monthly ?? 0) -
                        parseAmount(accountLimits.Outbound?.RemainingLimits?.Monthly ?? 0),
                    ),
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      }
    />
  );
};

export default TotalPaymentsCard;
